import React from 'react';
import { Link } from 'react-router-dom';
import { Tab, Tabs } from '@mui/material';
import { useTranslate } from 'react-admin';
import tokenService from '../services/tokenService';
import { canLicenseCourses } from './marketplaceVisibility';
import MaterialSymbol from '../components/common/MaterialSymbol';
import BuyerBrowsePanel from '../components/Marketplace/Buyer/BuyerBrowsePanel';
import BuyerLicensesPanel from '../components/Marketplace/Buyer/BuyerLicensesPanel';
import BuyerOrdersPanel from '../components/Marketplace/Buyer/BuyerOrdersPanel';
import '../components/Marketplace/marketplace-design.css';

export type MarketplaceSection = 'browse' | 'licenses' | 'orders';

export const MARKETPLACE_PATHS: Record<MarketplaceSection, string> = {
    browse: '/marketplace',
    licenses: '/marketplace/licenses',
    orders: '/marketplace/orders',
};

/**
 * Marktplatz aus Käufersicht (Marktplatz-Rework T6): Angebote durchsuchen (zwei
 * getrennte Bereiche öffentlich / Unternehmensgruppe), Meine Lizenzen sowie
 * Bestellungen und Rechnungen. Anbieten, Verkäufe und Abrechnung liegen im
 * Verkäufer-Dashboard (T5, `/marketplace/seller`). Kaufen, Lizenzen und
 * Rechnungen verlangen Company-Admin; der Content-Manager sieht nur das
 * Angebot (read-only).
 */
const MarketplacePage: React.FC<{ section?: MarketplaceSection }> = ({ section = 'browse' }) => {
    const translate = useTranslate();
    const canBuy = canLicenseCourses(tokenService.getRoles());
    const sections: MarketplaceSection[] = canBuy ? ['browse', 'licenses', 'orders'] : ['browse'];
    const active: MarketplaceSection = sections.includes(section) ? section : 'browse';

    return (
        <div className="mk-page">
            <div className="mk-head">
                <h1 className="oa-page-title" style={{ margin: 0 }}>{translate('marketplaceBuyer.title')}</h1>
                <div className="mk-acts">
                    <Link to="/marketplace/seller" className="mk-btn-o">
                        <MaterialSymbol name="storefront" />
                        {translate('marketplaceSeller.openDashboard')}
                    </Link>
                </div>
            </div>
            <p className="mk-sub">{translate('marketplaceBuyer.subtitle')}</p>

            <Tabs
                value={active}
                variant="scrollable"
                scrollButtons={false}
                aria-label={translate('marketplaceBuyer.title')}
                className="mk-tabs"
            >
                {sections.map((s) => (
                    <Tab
                        key={s}
                        value={s}
                        component={Link}
                        to={MARKETPLACE_PATHS[s]}
                        disableRipple
                        label={translate(`marketplaceBuyer.nav.${s}`)}
                    />
                ))}
            </Tabs>

            {!canBuy && active === 'browse' && (
                <div className="mk-alert info" role="note">
                    <MaterialSymbol name="info" />
                    <div className="body">{translate('marketplaceBuyer.readOnlyHint')}</div>
                </div>
            )}

            {active === 'browse' && <BuyerBrowsePanel canBuy={canBuy} />}
            {active === 'licenses' && <BuyerLicensesPanel />}
            {active === 'orders' && <BuyerOrdersPanel />}
        </div>
    );
};

export default MarketplacePage;
